/* eslint-disable react/prop-types, react/jsx-handler-names */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Select from 'react-select';
import { Theme, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import NoSsr from '@material-ui/core/NoSsr';
import TextField from '@material-ui/core/TextField';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import CancelIcon from '@material-ui/icons/Cancel';
import { emphasize } from '@material-ui/core/styles/colorManipulator';

const styles = (theme: any) => ({
  root: {
    flexGrow: 1,
    height: 10,
  },
  input: {
    display: 'flex',
    padding: 0,
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflow: 'hidden',
    '&>div':{
      padding: 0,
      fontSize: 12,
      height: 30,
    },
 
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
      0.08
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: 'absolute',
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: 'absolute',
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
});

function NoOptionsMessage(props: any) {
  return (
    <Typography color='textSecondary' className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }: any) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: any) {
  return (
    <TextField
      fullWidth
      disableUnderline={true}
      InputProps={{
        disableUnderline: true,
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props: any) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component='div'
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props: any) {
  return (
    <Typography color='textSecondary' className={props.selectProps.classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function SingleValue(props: any) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

function ValueContainer(props: any) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props: any) {
  
  if(props.data.isRemovable){
    
    return (
      <Chip
        tabIndex={-1}
        label={props.children}
        className={classNames(props.selectProps.classes.chip, {
          [props.selectProps.classes.chipFocused]: props.isFocused,
        })}
        onDelete={props.removeProps.onClick}
        deleteIcon={<CancelIcon {...props.removeProps} />}
      />
    );
  } else {
    
    return (
      <Chip
        tabIndex={-1}
        label={props.children}
        className={classNames(props.selectProps.classes.chip, {
          [props.selectProps.classes.chipFocused]: props.isFocused,
        })}
      />
    );
  }
  
}

function Menu(props: any) {
  return (
    <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

interface Props {
  classes: any;
  theme: any;
  suggestions: any[];
  placeholderText: '';
  onSelection: Function;
  loggedInUserFullName: string;
  courses?: any;
  values:{label:string,value:string,id:string} [],
  disabled?: boolean,
  textFieldProps: { [key: string]: any };
  notFound: string;
  isClearable: boolean;
  backspaceRemoves: boolean;
}

class SearchBarWithChipsGoal extends React.Component<Props> {
  state = {
    single: null,
    multi:this.props.values||null,
    suggestions: []
  };
  componentDidMount() {
    // 
    if (this.props.suggestions.length > 0) {
      const updatedSuggestions = this.prepareSuggestionList(this.props.suggestions);
      this.setState({
        suggestions: updatedSuggestions,
      });
    }
  }
  componentDidUpdate(prevProps: Props) {
      if(prevProps.suggestions !== this.props.suggestions){
           const updatedSuggestions = this.prepareSuggestionList(this.props.suggestions);
          this.setState({
            suggestions: updatedSuggestions
          })
        
      }

    if (prevProps.loggedInUserFullName !== this.props.loggedInUserFullName) {
      const firstChip = {
        value: `${this.props.loggedInUserFullName}`,
        label: `${this.props.loggedInUserFullName}`,
        id: null,
      };
      this.setState({
        multi: [firstChip],
      });
    }
    if(prevProps.values !== this.props.values) {
      this.setState({
        multi: this.props.values
      })
    }
  }

  handleChange = (name: any) => (value: any) => {
   this.setState(
      {
        [name]: value,
      },
      () => this.props.onSelection(this.state.multi)
    );
  };

  prepareSuggestionList = (suggestions: any[]) => {
    return suggestions.map((suggestion) => ({
      value: suggestion.value,
      label: suggestion.label,
      id: suggestion.id,
    }));
  };
  getOptions = () => {
    return this.props.courses ? this.props.courses : this.state.suggestions;
  };
  render() {
    const { classes, theme, textFieldProps = {}, notFound, isClearable, backspaceRemoves } = this.props;

    const selectStyles = {
      input: (base: any) => ({
        ...base,
        color: theme.palette.text.primary,
        '& input': {
          font: 'inherit',
        },
      }),
    };

    return (
      <div className={classes.root}>
        <NoSsr>
          <Select
            classes={classes}
            styles={selectStyles}
            textFieldProps={{
              label: '',
              InputLabelProps: {
                shrink: true,
              },
              ...textFieldProps
            }}
            options={this.getOptions()}
            components={components}
            value={this.state.multi}
            onChange={this.handleChange('multi')}
            placeholder={this.props.placeholderText}
            isDisabled = {this.props.disabled ? this.props.disabled : false}
            isMulti
            noOptionsMessage={() => notFound}
            isClearable={isClearable}
            backspaceRemovesValue={backspaceRemoves? backspaceRemoves : false}
          />
        </NoSsr>
      </div>
    );
  }
}

// SearchBarWithChipsGoal.propTypes = {
//   classes: PropTypes.object.isRequired,
//   theme: PropTypes.object.isRequired,
// };

// @ts-ignore
export default withStyles(styles, { withTheme: true })(SearchBarWithChipsGoal);
